import { useMemo } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import type { PortalDashboardStats } from '@tkpod/shared';

interface OrderTrendChartProps {
  data: PortalDashboardStats['dailyTrend'];
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (!active || !payload?.length || !label) return null;
  return (
    <div className="rounded-lg border border-border bg-surface-primary px-3 py-2 shadow-md">
      <p className="text-xs font-medium text-text-secondary">
        {formatDateLabel(label)}
      </p>
      <p className="text-sm font-semibold text-text-primary">
        {payload[0].value} {payload[0].value === 1 ? 'order' : 'orders'}
      </p>
    </div>
  );
}

export function OrderTrendChart({ data }: OrderTrendChartProps) {
  const chartData = useMemo(
    () => data.map((d) => ({ ...d, label: formatDateLabel(d.date) })),
    [data],
  );

  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="h-[180px] w-full sm:h-[200px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 8, right: 4, bottom: 0, left: -20 }}
        >
          <defs>
            <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-brand-500, #6366f1)" stopOpacity={0.25} />
              <stop offset="95%" stopColor="var(--color-brand-500, #6366f1)" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="var(--color-border, #e5e7eb)"
            strokeOpacity={0.5}
          />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateLabel}
            tick={{ fontSize: 11, fill: 'var(--color-text-tertiary, #9ca3af)' }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={40}
          />
          <YAxis
            allowDecimals={false}
            domain={[0, maxCount + 1]}
            tick={{ fontSize: 11, fill: 'var(--color-text-tertiary, #9ca3af)' }}
            axisLine={false}
            tickLine={false}
            width={40}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="count"
            stroke="var(--color-brand-500, #6366f1)"
            strokeWidth={2}
            fill="url(#trendFill)"
            dot={false}
            activeDot={{
              r: 4,
              stroke: 'var(--color-brand-500, #6366f1)',
              strokeWidth: 2,
              fill: 'var(--color-surface-primary, #fff)',
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
